import { isEmpty } from '@ember/utils';
import type { CostSettingProfile } from 'lumen/models';

export interface CostProfileSelection {
  autoCostProfile: CostSettingProfile | null;
  costProfile: CostSettingProfile | null;
  userCostProfile: CostSettingProfile | null;
}

export interface AggregatedCostProfile extends CostProfileSelection {
  isMixed: boolean;
}

const EMPTY_AGGREGATE: AggregatedCostProfile = {
  autoCostProfile: null,
  costProfile: null,
  isMixed: false,
  userCostProfile: null,
};

function hasSameCostProfile(entries: CostProfileSelection[]): boolean {
  return entries.every(
    (entry) => entry.costProfile === entries[0]?.costProfile,
  );
}

export function aggregateParentCostProfile(
  entries: CostProfileSelection[],
): AggregatedCostProfile {
  const [first] = entries;

  if (!first) {
    return EMPTY_AGGREGATE;
  }

  const isMixed = !hasSameCostProfile(entries);
  const costProfile = isMixed ? null : first.costProfile;

  // If all auto-mapped children share the same cost profile, the parent is auto-mapped to it.
  const autoCostProfiles = new Set(
    entries
      .filter(
        (entry) =>
          !isEmpty(entry.autoCostProfile) && isEmpty(entry.userCostProfile),
      )
      .map((entry) => entry.autoCostProfile),
  );
  const autoCostProfile =
    autoCostProfiles.size === 1 ? ([...autoCostProfiles][0] ?? null) : null;

  // Mixed children have no single user value, like the commissionable parent row.
  // Otherwise all children share one profile, so the first child's user value stands for all.
  const userCostProfile =
    !isMixed &&
    isEmpty(autoCostProfile) &&
    entries.some((entry) => !isEmpty(entry.userCostProfile))
      ? first.userCostProfile
      : null;

  return { autoCostProfile, costProfile, isMixed, userCostProfile };
}

// The bulk edit row shows no automation visuals.
export function aggregateBulkCostProfile(
  entries: CostProfileSelection[],
): AggregatedCostProfile {
  const [first] = entries;

  if (!first) {
    return EMPTY_AGGREGATE;
  }

  const isMixed = !hasSameCostProfile(entries);
  const costProfile = isMixed ? null : first.costProfile;
  const hasSameAuto = entries.every(
    (entry) => entry.autoCostProfile === first.autoCostProfile,
  );

  return {
    autoCostProfile: hasSameAuto ? first.autoCostProfile : null,
    costProfile,
    isMixed,
    // A same-profile mix of user and auto shows as user-mapped in bulk edit.
    userCostProfile: costProfile,
  };
}

// Re-selecting the auto-mapped profile isn't a change, so nothing is stored for the entry.
export function getUserCostProfileToStore(
  entry: CostProfileSelection,
  costProfile: CostSettingProfile | null,
): CostSettingProfile | null {
  return costProfile === entry.autoCostProfile && isEmpty(entry.userCostProfile)
    ? null
    : costProfile;
}
